import * as yup from "yup";
import { formatDate } from "../../UI/Utils/formatDate.ts";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col } from "../../UI/Layout/Col.tsx";
import { TextInput } from "../../UI/TextInput.tsx";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { Row } from "../../UI/Layout/Row.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { useWithLoading } from "../../UI/Loading/useWithLoading.ts";
import dayjs from "dayjs";
import { useCalculatePlanGivenSlot } from "./useCalculatePlanGivenSlot.ts";
import { isDefined } from "../../isDefined.ts";
import { convertToInt } from "../../DataUtils/convertToInt.ts";
import { SpinnerContainer } from "../../UI/Loading/SpinnerContainer.tsx";
import { ClassRoomPlanSelect } from "./ClassRoomPlanSelect.tsx";
import { ClassRoomPlanWarrantyConfigDTO } from "./ClassRoomPlanWarrantyConfigDTO.tsx";
import { HorizontalLineDivider } from "../../UI/HorizontalLineDivider.tsx";
import { ClassRoomPlanWarrantyConfigInput } from "./ClassRoomPlanWarrantyConfigInput.tsx";
import { Select } from "@normasteaching/norma-ui";
import { useEffect, useState } from "react";
import { PlanBonusesEditor } from "./PlanBonusesEditor.tsx";
import { ManualBonusFormInput, formatBonus } from "./planBonusUtils.ts";
import {
  getStartOfWeek,
  getEndOfWeek,
  computeEndAtWeekFromStartAndWeeks,
} from "./planWeekDateUtils.ts";

export type ClassRoomPlanFormInputs = {
  startAtWeek: Date;
  endAtWeek: Date;
  weeks: number;
  lessonsPerWeek: number;
  planToRecoverId?: string | null;
  warrantyConfig?: ClassRoomPlanWarrantyConfigDTO;
  unlimitedLessonsPerWeek?: {
    enabled: boolean;
    totalLessons?: number;
  };
  bonuses: ManualBonusFormInput[];
};

enum YesNo {
  Yes = "Yes",
  No = "No",
}

const schema = yup
  .object({
    startAtWeek: yup
      .date()
      .typeError("Devi specificare una data valida")
      .required("Inserisci una data di inizio"),
    endAtWeek: yup
      .date()
      .typeError("Devi specificare una data valida")
      .required("Inserisci una data di fine"),
    weeks: yup
      .number()
      .typeError("Devi specificare un numero")
      .integer()
      .min(1, "Minimo 1 settimana")
      .required("Inserisci la durata in settimane"),
    lessonsPerWeek: yup
      .number()
      .typeError("Devi specificare un numero")
      .integer()
      .required("Inserisci il numero di lezioni a settimana"),
    planToRecoverId: yup.string().nullable(),
    warrantyConfig: yup
      .object()
      .shape({
        enabled: yup.boolean().optional(),
        minAttendedLessons: yup.number().when("enabled", {
          is: (v: unknown) => !!v,
          then: (yup) => yup.required(),
        }),
      })
      .optional(),
    unlimitedLessonsPerWeek: yup
      .object({
        enabled: yup.boolean().required(),
        totalLessons: yup.number().when("enabled", {
          is: (v: unknown) => !!v,
          then: (yup) =>
            yup
              .required("Inserire il numero totale di lezioni")
              .min(1, "Deve essere almeno 1"),
        }),
      })
      .optional(),
    bonuses: yup.array().optional().default([]),
  })
  .required();

type Props = {
  defaultValues?: Partial<ClassRoomPlanFormInputs>;
  onSave: (data: ClassRoomPlanFormInputs) => Promise<void>;
  disabledMap?: {
    [key in keyof ClassRoomPlanFormInputs]?: boolean;
  };
  userId: string;
};

export function CreateClassRoomPlanForm({
  defaultValues,
  onSave,
  disabledMap,
  userId,
}: Props) {
  const [bonuses, setBonuses] = useState<ManualBonusFormInput[]>(
    defaultValues?.bonuses ?? [],
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
    control,
  } = useForm<ClassRoomPlanFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...enrichDefaultValuesWithDefaultLessonPerWeekIfNotSpecified(
        enrichDefaultValuesWithDefaultDatesIfNotSpecified(
          defaultValues ?? ({} as ClassRoomPlanFormInputs),
        ),
      ),
    },
  });

  function enrichDefaultValuesWithDefaultDatesIfNotSpecified(
    prevDefaultValue: Partial<ClassRoomPlanFormInputs>,
  ): Partial<ClassRoomPlanFormInputs> {
    if (prevDefaultValue.startAtWeek || prevDefaultValue.endAtWeek)
      return prevDefaultValue;
    const weeks = prevDefaultValue.weeks ?? 26;
    const start = getStartOfWeek(
      dayjs().add(1, "week").startOf("week").toDate(),
    ).toDate();
    const end = computeEndAtWeekFromStartAndWeeks(start, weeks);
    return {
      ...prevDefaultValue,
      startAtWeek: start,
      endAtWeek: end,
      weeks,
    };
  }

  function enrichDefaultValuesWithDefaultLessonPerWeekIfNotSpecified(
    prevDefaultValue: Partial<ClassRoomPlanFormInputs>,
  ): Partial<ClassRoomPlanFormInputs> {
    if (prevDefaultValue.lessonsPerWeek) return prevDefaultValue;
    return {
      ...prevDefaultValue,
      lessonsPerWeek: 2,
    };
  }

  const startDate = watch("startAtWeek");
  const endDate = watch("endAtWeek");
  const weeks = watch("weeks");
  const lessonsPerWeek = watch("lessonsPerWeek");
  const planToRecoverId = watch("planToRecoverId");
  const unlimitedLessonsPerWeekEnabled =
    watch("unlimitedLessonsPerWeek.enabled") ?? false;
  const totalLessons = watch("unlimitedLessonsPerWeek.totalLessons");

  useEffect(() => {
    if (!isDefined(startDate) || !dayjs(startDate).isValid()) return;
    if (!isDefined(weeks) || isNaN(Number(weeks)) || Number(weeks) < 1) return;
    setValue(
      "endAtWeek",
      computeEndAtWeekFromStartAndWeeks(startDate, Number(weeks)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, weeks]);

  const { withLoading, loading } = useWithLoading();

  const { calculatedGivenSlot, loading: loadingGivenSlotCount } =
    useCalculatePlanGivenSlot({
      startedAtWeekUtc:
        isDefined(startDate) && dayjs(startDate).isValid()
          ? getStartOfWeek(startDate).toISOString()
          : undefined,
      endedAtWeekUtc:
        isDefined(endDate) && dayjs(endDate).isValid()
          ? getEndOfWeek(endDate).toISOString()
          : undefined,
      lessonsPerWeek: isDefined(lessonsPerWeek)
        ? convertToInt(lessonsPerWeek)
        : undefined,
      unlimitedLessonPerWeek: {
        enabled: unlimitedLessonsPerWeekEnabled,
        totalLessons: isDefined(totalLessons)
          ? convertToInt(totalLessons)
          : undefined,
      },
    });

  function mapData(data: ClassRoomPlanFormInputs): ClassRoomPlanFormInputs {
    return {
      ...data,
      startAtWeek: getStartOfWeek(data.startAtWeek).toDate(),
      endAtWeek: getEndOfWeek(data.endAtWeek).toDate(),
      bonuses,
    };
  }

  const onSubmit: SubmitHandler<ClassRoomPlanFormInputs> = async (data) => {
    await withLoading(async () => onSave(mapData(data)));
  };

  function getPlanInfoLines(
    sDate?: Date,
    eDate?: Date,
    calculatedGivenSlot?: number,
    planWeeks?: number,
    planBonuses?: ManualBonusFormInput[],
  ): string[] {
    if (
      !isDefined(sDate) ||
      !isDefined(eDate) ||
      !dayjs(sDate).isValid() ||
      !dayjs(eDate).isValid()
    )
      return [];
    const startDate = getStartOfWeek(sDate);
    const endDate = getEndOfWeek(eDate);
    if (!startDate.isValid() || !endDate.isValid()) {
      return [];
    }

    const lines: string[] = [
      `The plan you are creating will go from the week starting ${formatDate(startDate, { withWeekday: true })} to the week ending ${formatDate(endDate, { withWeekday: true })}${isDefined(planWeeks) ? ` (${planWeeks} weeks)` : ""}`,
    ];
    if (calculatedGivenSlot)
      lines.push(
        `In total, the user will be able to book ${calculatedGivenSlot} hours of lessons.`,
      );
    if (planBonuses && planBonuses.length > 0)
      lines.push(`Bonuses applied: ${planBonuses.map(formatBonus).join(", ")}`);
    return lines;
  }

  return (
    <>
      <form>
        <Col>
          <Row>
            <InputWithLabel label="Start date">
              <Controller
                disabled={disabledMap?.startAtWeek}
                control={control}
                render={({ field }) => {
                  return (
                    <TextInput
                      disabled={disabledMap?.startAtWeek}
                      errorMessage={errors.startAtWeek?.message}
                      onChange={(e) => field.onChange(e)}
                      value={
                        field.value
                          ? dayjs(field.value).format("YYYY-MM-DD")
                          : ""
                      }
                      type={"date"}
                    />
                  );
                }}
                name={"startAtWeek"}
              />
            </InputWithLabel>
            <InputWithLabel label="Weeks">
              <TextInput
                disabled={disabledMap?.weeks}
                errorMessage={errors.weeks?.message}
                {...register("weeks")}
                type={"number"}
              />
            </InputWithLabel>
          </Row>
          {unlimitedLessonsPerWeekEnabled ? null : (
            <Row>
              <InputWithLabel label="Weekly lessons count">
                <TextInput
                  disabled={disabledMap?.lessonsPerWeek}
                  errorMessage={errors.lessonsPerWeek?.message}
                  {...register("lessonsPerWeek")}
                  type={"number"}
                />
              </InputWithLabel>
            </Row>
          )}
          <Row>
            <InputWithLabel label="Plan to recover">
              <Controller
                control={control}
                render={({ field }) => {
                  return (
                    <ClassRoomPlanSelect
                      selectedPlanId={field.value ?? undefined}
                      userId={userId}
                      onChange={field.onChange}
                      filterPlans={(plans) =>
                        plans.filter((p) => !p.planToRecoverId)
                      }
                    />
                  );
                }}
                name={"planToRecoverId"}
              />
            </InputWithLabel>
          </Row>
          {planToRecoverId ? null : (
            <div className={"w-full"}>
              <div className={"flex items-center space-x-4"}>
                <HorizontalLineDivider />
                <p className={"whitespace-nowrap font-bold"}>
                  Warranty Configuration
                </p>
                <HorizontalLineDivider />
              </div>
              <Controller
                render={({ field }) => (
                  <ClassRoomPlanWarrantyConfigInput
                    warrantyConfig={field.value}
                    onChange={field.onChange}
                  />
                )}
                name={"warrantyConfig"}
                control={control}
              />
            </div>
          )}
          <Row>
            <div className={"flex flex-col space-y-4 w-full"}>
              <div className={"w-full"}>
                <div className={"flex items-center space-x-4"}>
                  <HorizontalLineDivider />
                  <p className={"whitespace-nowrap font-bold"}>
                    Unlimited lessons per week
                  </p>
                  <HorizontalLineDivider />
                </div>
              </div>

              <div className={"flex flex-col space-y-4"}>
                <InputWithLabel label={"Enabled"}>
                  <Controller
                    render={({ field }) => {
                      return (
                        <Select<YesNo>
                          value={field.value ? YesNo.Yes : YesNo.No}
                          onChange={(v) =>
                            v === YesNo.Yes
                              ? field.onChange(true)
                              : field.onChange(false)
                          }
                          options={[
                            {
                              id: YesNo.No,
                              name: "No",
                            },
                            {
                              id: YesNo.Yes,
                              name: "Yes",
                            },
                          ]}
                          errorMessage={
                            errors?.unlimitedLessonsPerWeek?.enabled?.message
                          }
                        />
                      );
                    }}
                    name={"unlimitedLessonsPerWeek.enabled"}
                    control={control}
                  />
                </InputWithLabel>
                {!unlimitedLessonsPerWeekEnabled ? null : (
                  <InputWithLabel label={"Total lessons"}>
                    <TextInput
                      {...register("unlimitedLessonsPerWeek.totalLessons")}
                      errorMessage={
                        errors?.unlimitedLessonsPerWeek?.totalLessons?.message
                      }
                      type={"number"}
                    />
                  </InputWithLabel>
                )}
              </div>
            </div>
          </Row>
          <Row>
            <PlanBonusesEditor bonuses={bonuses} onChange={setBonuses} />
          </Row>
          <Row>
            <SpinnerContainer loading={loadingGivenSlotCount}>
              <div className={"bg-blue-200 rounded-md p-3"}>
                {getPlanInfoLines(
                  startDate,
                  endDate,
                  calculatedGivenSlot,
                  isDefined(weeks) ? Number(weeks) : undefined,
                  bonuses,
                ).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </SpinnerContainer>
          </Row>
          <Row>
            <div className={"flex w-full justify-end"}>
              <PrimaryButton
                loading={loading}
                onClick={handleSubmit(onSubmit)}
                label={"Create plan"}
              />
            </div>
          </Row>
        </Col>
      </form>
    </>
  );
}
